import React from "react";
import { Box, CircularProgress, Grid, Stack, Typography } from "@mui/material";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import DashboardPanelHeader from "components/DashboardPanelHeader";

const TargetActualSqft = ({ data, isError, isLoading }) => {

    const targetValue = data?.targetSqft || 0;
    const actualValue = data?.actualSqft || 0;

    const chartData = [
        {
            label: "Target (Sqft)",
            actual: targetValue,
            color: "#3B82F6",
        },
        {
            label: "Actual (Sqft)",
            actual: actualValue,
            color: "#22C55E",
        },
    ];


    const chartOptions = {
        chart: {
            type: "solidgauge",
            height: 260,
        },
        title: null,
        pane: {
            center: ["50%", "67%"],
            size: "90%",
            startAngle: -120,
            endAngle: 120,
            background: [
                {
                    outerRadius: "110%",
                    innerRadius: "85%",
                    backgroundColor: "#EEF0FF",
                    shape: "arc",
                },
                {
                    outerRadius: "84%",
                    innerRadius: "60%",
                    backgroundColor: "#F5F7FF",
                    shape: "arc",
                },
            ],
        },
        yAxis: {
            min: 0,
            max: Math.max(targetValue, actualValue),
            lineWidth: 0,
            tickPositions: [],
            overshoot: 90,
            wrap: false,
        },
        plotOptions: {
            solidgauge: {
                dataLabels: { enabled: false },
                stickyTracking: true,
                linecap: "round",
            },
        },
        series: [
            {
                name: "Target (Sqft)",
                data: [
                    {
                        y: targetValue,
                        color: "#3B82F6",
                        customValue: targetValue,
                        radius: "110%",
                        innerRadius: "85%",
                    },
                ],
            },
            {
                name: "Actual (Sqft)",
                data: [
                    {
                        y: actualValue,
                        color: "#22C55E",
                        customValue: actualValue,
                        radius: "84%",
                        innerRadius: "60%",
                    },
                ],
            },
        ],
        tooltip: {
            useHTML: true,
            formatter: function () {
                const point = this.point;
                return `
                    <div style="font-family: Outfit; font-size: 12px;">
                        <span style="color:${point.color}">●</span>
                        <b>${this.series.name}</b>: ${point.customValue.toLocaleString()} Sqft
                    </div>
                    `;
            },
        },
        credits: { enabled: false },
    };

    const LocalTitleBar = () => {
        return (
            <Box
                sx={{
                    width: "100%",
                    borderBottom: "1px solid #E4E4E4",
                    p: 1.5,
                    display: "flex",
                    justifyContent: "space-between",
                    boxSizing: "border-box",
                }}
            >
                <Box>
                    <Typography
                        sx={{
                            fontFamily: "Outfit, sans-serif",
                            fontWeight: 600,
                            fontSize: "20px",
                        }}
                    >
                        Lease Area Overview
                    </Typography>

                    <Typography
                        sx={{
                            fontFamily: "Outfit, sans-serif",
                            fontWeight: 300,
                            fontSize: "14px",
                            color: "#7D7F81",
                        }}
                    >
                        Comparison of planned vs achieved construction area in Sqft
                    </Typography>
                </Box>
            </Box>
        );
    };

    return (
        <>
            <Box
                sx={{
                    background: "#fff",
                    border: "1px solid #E4E4E4",
                    borderRadius: 1,
                }}
            >
                <DashboardPanelHeader eyebrow="Area delivery" title="Leased Area Attainment" subtitle="Compare target and achieved leased area across the commercial plan." />
                {isLoading && (
                    <Box
                        sx={{
                            minHeight: "100px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <CircularProgress size={32} />
                    </Box>
                )}
                {isError && (
                    <Box
                        sx={{
                            minHeight: "100px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            px: 2,
                        }}
                    >
                        <Typography
                            sx={{
                                fontFamily: "Outfit, sans-serif",
                                fontSize: "14px",
                                color: "#FF4F8B",
                                textAlign: "center",
                            }}
                        >
                            Failed to load data. Please try again.
                        </Typography>
                    </Box>
                )}
                {!isError && !isLoading && <Grid
                    container
                    sx={{
                        alignItems: "center",
                        justifyContent: "space-between",
                        px: 2,
                        py: 2,
                    }}
                    spacing={3}
                >
                    <Grid
                        size={{ xs: 12, md: 6 }}
                        sx={{
                            pr: { md: 4 },
                        }}
                    >
                        <Stack spacing={2}>
                            {chartData.map((item, i) => (
                                <Box
                                    key={i}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        border: "1px solid #EAEAEA",
                                        p: "8px 10px",
                                        borderRadius: "4px",
                                        backgroundColor: "#fff",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1.5,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 12,
                                                height: 12,
                                                borderRadius: "50%",
                                                background: item.color,
                                            }}
                                        />
                                        <Typography
                                            sx={{
                                                fontSize: "14px",
                                                fontWeight: 400,
                                                lineHeight: "18px",
                                            }}
                                        >
                                            {item.label}
                                        </Typography>
                                    </Box>

                                    <Box
                                        sx={{
                                            minWidth: 50,
                                            height: 20,
                                            borderRadius: "5px",
                                            backgroundColor: "#E7E7EB",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            fontSize: "11px",
                                            fontWeight: 600,
                                        }}
                                    >
                                        {item.actual.toLocaleString()}
                                    </Box>
                                </Box>
                            ))}
                        </Stack>
                    </Grid>
                    <Grid
                        size={{ xs: 12, md: 6 }}
                        sx={{
                            pr: { md: 4 },
                        }}
                    >
                        <HighchartsReact highcharts={Highcharts} options={chartOptions} />
                    </Grid>
                </Grid>}
            </Box>
        </>
    );
};

export default TargetActualSqft;
